import React, { useState, useContext } from "react";
import {
  Box,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Card,
  CardMedia,
  CardActions,
  Grid,
  Typography,
  Button,
  InputAdornment,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { AppContext } from "../../../context/AppContext";

const SectionDetails = () => {
  const { sections, refreshSections, csrfFetch } = useContext(AppContext);

  const [title, setTitle] = useState("");
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [titleError, setTitleError] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editSequential, setEditSequential] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(URL.createObjectURL(file));
      setImageFile(file);
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImageFile(null);
  };

  const handleAddSection = async () => {
    if (!title.trim()) {
      setTitleError(true);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", title.trim());
      if (imageFile) formData.append("image", imageFile);

      const response = await csrfFetch("/categorias/secciones/agregar/", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error();

      setSuccessMessage("Sección añadida correctamente.");
      setTitle("");
      setImage(null);
      setImageFile(null);
      await refreshSections();
    } catch {
      setErrorMessage("No se pudo agregar la sección.");
    }
  };

  const handleOpenEditModal = (section) => {
    setEditSection(section);
    setEditTitle(section.name);
    setEditSequential(section.sequential_code);
    setEditImage(section.image);
    setEditOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditOpen(false);
    setEditSection(null);
    setEditImageFile(null);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImage(URL.createObjectURL(file));
      setEditImageFile(file);
    }
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) {
      setErrorMessage("El nombre no puede estar vacío.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", editTitle.trim());
      formData.append("sequential_code", editSequential.trim());
      if (editImageFile) formData.append("image", editImageFile);

      const response = await csrfFetch(`/categorias/secciones/${editSection.id}/editar/`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error();

      setSuccessMessage("Sección actualizada correctamente.");
      handleCloseEditModal();
      await refreshSections();
    } catch {
      setErrorMessage("No se pudo actualizar la sección.");
    }
  };

  const handleDeleteSection = async () => {
    if (!editSection) return;

    try {
      const response = await csrfFetch(`/categorias/secciones/${editSection.id}/eliminar/`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error();

      setSuccessMessage("Sección eliminada correctamente.");
      handleCloseEditModal();
      await refreshSections();
    } catch {
      setErrorMessage("No se puede eliminar esta sección porque tiene productos asociados.");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
        Gestión de Secciones
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Nombre de la sección"
            variant="outlined"
            fullWidth
            value={title}
            onChange={(e) => {
              setTitleError(false);
              setTitle(e.target.value);
            }}
            error={titleError}
            helperText={titleError ? "Este campo es obligatorio" : ""}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: title && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setTitle("");
                      setTitleError(false);
                    }}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            component="label"
            startIcon={<AddPhotoAlternateIcon />}
            sx={{
              textTransform: "none",
              bgcolor: "#1565C0",
              "&:hover": { bgcolor: "#0D47A1" },
              px: 2.2,
              mb: 2,
              width: "100%",
              fontWeight: 700,
            }}
          >
            {image ? "Cambiar Imagen" : "Subir Imagen"}
            <input type="file" hidden accept="image/*" onChange={handleImageChange} />
          </Button>

          <Box
            sx={{
              width: "100%",
              height: 200,
              borderRadius: 2,
              border: "2px dashed #999",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              mb: 2,
            }}
          >
            {image ? (
              <Card sx={{ width: "100%", height: "100%" }}>
                <CardMedia
                  component="img"
                  image={image}
                  alt="Vista previa"
                  sx={{ objectFit: "contain", height: "100%", width: "100%" }}
                />
                <CardActions sx={{ display: "flex", justifyContent: "center" }}>
                  <IconButton color="error" onClick={handleRemoveImage}>
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            ) : (
              <Typography variant="body2" sx={{ color: "#666" }}>
                Imagen opcional
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleAddSection}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              bgcolor: "#1565C0",
              "&:hover": { bgcolor: "#0D47A1" },
              px: 2.2,
              fontWeight: 700,
            }}
            disabled={!title.trim()}
          >
            Añadir Sección
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              maxHeight: 250,
              overflowY: "auto",
              p: 2,
              borderRadius: 2,
              backgroundColor: "#f5f5f5",
            }}
          >
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
              Secciones Creadas
            </Typography>
            {sections.map((section) => (
              <Typography
                key={section.id}
                variant="body2"
                sx={{ mb: 1, cursor: "pointer" }}
                onClick={() => handleOpenEditModal(section)}
              >
                - {section.name}
              </Typography>
            ))}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={editOpen} onClose={handleCloseEditModal} maxWidth="md" fullWidth>
        <DialogTitle>Editar Sección</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Secuencial"
            value={editSequential}
            onChange={(e) => setEditSequential(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Nombre"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Button
            component="label"
            variant="contained"
            startIcon={<AddPhotoAlternateIcon />}
            sx={{ mb: 2 }}
          >
            Cambiar Imagen
            <input type="file" hidden accept="image/*" onChange={handleEditImageChange} />
          </Button>

          {editImage && (
            <Card sx={{ width: "100%", height: 300 }}>
              <CardMedia
                component="img"
                image={editImage}
                alt="Vista previa"
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Card>
          )}
        </DialogContent>

        <DialogActions>
          <Button color="error" onClick={handleDeleteSection}>
            Eliminar
          </Button>
          <Button color="primary" onClick={handleSaveEdit}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{errorMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SectionDetails;